import { MenuItem, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import React from 'react';
import { FieldErrors, UseFormRegister } from 'react-hook-form';
import { FormFields } from '../IGPRScore.interface';
import { patterns } from '../utils/constants';
import { avoidBadCharacters, validateKeydown } from '../utils/common';
import styles from '../IGPRScore.module.scss';

interface InputGIProps {
  register: UseFormRegister<FormFields>;
  errors: FieldErrors<FormFields>;
}

export const InputGeneralInformation: React.FC<InputGIProps> = ({
  register,
  errors
}) => (
  <section id="general-information">
    <h2 className="h2">Important readings</h2>
    <div className="py-5">
      <div className={styles.formReadingsRoot}>
        <TextField
          sx={{
            '& .MuiInputBase-root.MuiOutlinedInput-root': {
              borderRadius: '1.75rem'
            }
          }}
          id="bmi-textfield"
          label="BMI reading"
          variant="outlined"
          {...register('bmi', {
            required: 'BMI is required',
            pattern: {
              value: patterns.decimal,
              message: 'The value must have numbers'
            },
            validate: avoidBadCharacters
          })}
          onKeyDownCapture={(event) => {
            const inputTarget = event.target as HTMLInputElement;
            const keyCode = event.keyCode ?? event.which;
            if (
              isNaN(Number(event.key)) &&
              event.key !== 'Backspace' &&
              event.key !== '.' &&
              !event.key.includes('Arrow')
            ) {
              event.stopPropagation();
              event.preventDefault();
            }
            if (keyCode === 190 && inputTarget.value.includes('.')) {
              return event.preventDefault();
            }
          }}
        />
        <TextField
          sx={{
            '& .MuiInputBase-root.MuiOutlinedInput-root': {
              borderRadius: '1.75rem'
            }
          }}
          select
          fullWidth
          label="Unit"
          defaultValue=""
          value={'kg/m²'}
          disabled
        >
          <MenuItem value={'kg/m²'}>kg/m²</MenuItem>
        </TextField>
        <Typography sx={{ color: 'red' }} className="error caption-sm-r mb-3">
          {errors.bmi?.message?.toString()}
        </Typography>
      </div>

      <div className={styles.formReadingsRoot}>
        <TextField
          sx={{
            '& .MuiInputBase-root.MuiOutlinedInput-root': {
              borderRadius: '1.75rem'
            }
          }}
          id="cholesterol-textfield"
          label="Cholesterol reading"
          variant="outlined"
          {...register('cholesterol', {
            required: 'Cholesterol is required',
            pattern: {
              value: patterns.decimal,
              message: 'The value must have numbers'
            },
            validate: avoidBadCharacters
          })}
          onKeyDownCapture={validateKeydown}
        />
        <TextField
          sx={{
            '& .MuiInputBase-root.MuiOutlinedInput-root': {
              borderRadius: '1.75rem'
            }
          }}
          select
          fullWidth
          label="Unit"
          defaultValue=""
          value={'mmol/L'}
          disabled
        >
          <MenuItem value={'mg/dl'}>mg/dl</MenuItem>
          <MenuItem value={'mmol/L'}>mmol/L</MenuItem>
        </TextField>
        <Typography sx={{ color: 'red' }} className="error caption-sm-r mb-3">
          {errors.cholesterol?.message?.toString()}
        </Typography>
      </div>

      <div className={styles.formReadingsRoot}>
        <TextField
          sx={{
            '& .MuiInputBase-root.MuiOutlinedInput-root': {
              borderRadius: '1.75rem'
            }
          }}
          id="bloodpressure-textfield"
          label="Blood pressure reading"
          variant="outlined"
          {...register('blood_pressure', {
            required: 'Blood pressure is required',
            pattern: {
              value: patterns.blood_pressure,
              message: 'Should have this pattern 120/20'
            },
            validate: avoidBadCharacters
          })}
          onKeyDownCapture={validateKeydown}
        />
        <TextField
          sx={{
            '& .MuiInputBase-root.MuiOutlinedInput-root': {
              borderRadius: '1.75rem'
            }
          }}
          select
          fullWidth
          label="Unit"
          defaultValue=""
          value={'mmHg'}
          disabled
        >
          <MenuItem value={'mmHg'}>mmHg</MenuItem>
        </TextField>
        <Typography sx={{ color: 'red' }} className="error caption-sm-r mb-3">
          {errors.blood_pressure?.message?.toString()}
        </Typography>
      </div>

      <div className={styles.formReadingsRoot}>
        <TextField
          sx={{
            '& .MuiInputBase-root.MuiOutlinedInput-root': {
              borderRadius: '1.75rem'
            }
          }}
          id="bloodsugarlevel-textfield"
          label="Blood sugar level reading"
          variant="outlined"
          {...register('blood_sugar', {
            required: 'Blood sugar level is required',
            pattern: {
              value: patterns.decimal,
              message: 'The value must have numbers'
            },
            validate: avoidBadCharacters
          })}
          onKeyDownCapture={validateKeydown}
        />
        <TextField
          sx={{
            '& .MuiInputBase-root.MuiOutlinedInput-root': {
              borderRadius: '1.75rem'
            }
          }}
          select
          fullWidth
          label="Unit"
          defaultValue=""
          value={'mmol/mol'}
          disabled
        >
          <MenuItem value={'mmol/mol'}>mmol/mol</MenuItem>
        </TextField>
        <Typography sx={{ color: 'red' }} className="error caption-sm-r mb-3">
          {errors.blood_sugar?.message?.toString()}
        </Typography>
      </div>
    </div>
  </section>
);

export default InputGeneralInformation;
